// ---------------------------------------------------------------------------
// new-project-wizard-page.tsx — Page-local creation wizard for /studio/new.
// Replaces the old NewProjectModal entry point.
//
// Architecture:
//   WizardProvider wraps everything → state lives in reducer context.
//   StepperSidebar (left, 320px) + main scrollable area (right).
//   FooterNav (sticky bottom, inside main area).
//   Step panels fade+slide in on transition (CSS animation).
// ---------------------------------------------------------------------------

import React, { useState, useCallback } from 'react'
import { WizardProvider, useWizard } from './wizard-state'
import { StepperSidebar } from './stepper-sidebar'
import { FooterNav } from './footer-nav'
import { Step1Start } from './steps/step-1-start'
import { Step2Template } from './steps/step-2-template'
import { Step2Scratch } from './steps/step-2-scratch'
import { Step3Details } from './steps/step-3-details'
import { Step4Review } from './steps/step-4-review'
import { createEmptyProject } from '../lib/create-empty-project'
import { createProjectFromTemplate } from '../lib/create-project-from-template'
import { useStudioStore } from '@/store/studio-store'
import {
  StudioGenerationBusyError,
  startAppGenerationFromPrompt,
} from '@/features/chat/services/start-app-generation-from-prompt'
import {
  appTargetFromRuntimeTargets,
  createStudioProjectPlan,
} from '../lib/studio-project-plan'
import {
  planningEvidenceFromMetadata,
  planningEvidenceFromProgress,
  type PlanningEvidence,
} from '../lib/planning-evidence'
import { handleProjectCreateError } from '@/store/project-create-error'
import { toast } from '@/store/toast-store'
import { cancelStudioNewWork } from '@/lib/studio-navigation'
import { useStudioLocale } from '@/i18n'
import styles from './new-project-wizard-page.module.css'

function WizardContent() {
  const copy = useStudioLocale().strings.newProject
  const { state, dispatch } = useWizard()
  const { step, source } = state
  const openServerProject = useStudioStore((s) => s.openServerProject)
  const signOut = useStudioStore((s) => s.signOut)

  const [success, setSuccess] = useState(false)
  const [createdProjectId, setCreatedProjectId] = useState<string | null>(null)
  const [progressMessage, setProgressMessage] = useState<string | null>(null)
  const [planningEvidence, setPlanningEvidence] =
    useState<PlanningEvidence | null>(null)

  const buildProjectPlan = useCallback(() => {
    const projectName = state.projectName.trim() || copy.errors.untitled
    return createStudioProjectPlan({
      name: projectName,
      intent: state.projectDescription,
      appClass: state.appClass,
      runtimeTargets: state.runtimeTargets,
      designSystemId:
        state.selectedDesignSystemRelease?.packageId ??
        state.selectedPresetId ??
        undefined,
      stylePresetId: state.selectedPresetId ?? undefined,
      designSystemRelease: state.selectedDesignSystemRelease ?? undefined,
      templateId:
        state.source === 'template' ? state.selectedTemplateId : undefined,
      securityProfile: state.securityProfile,
    })
  }, [state, copy])

  function handleCancel() {
    cancelStudioNewWork()
  }

  const createTemplateOrBlankProject = useCallback(async (): Promise<
    string | undefined
  > => {
    let projectId: string | undefined
    const projectName = state.projectName.trim() || copy.errors.untitled
    const studioProjectPlan = buildProjectPlan()
    const appTarget = appTargetFromRuntimeTargets(
      studioProjectPlan.runtimeTargets,
    )

    if (state.source === 'template' && state.selectedTemplateId) {
      const result = await createProjectFromTemplate({
        templateId: state.selectedTemplateId,
        projectName,
        description: state.projectDescription.trim() || undefined,
        appTarget,
        studioProjectPlan,
      })
      if (!result.success || !result.projectId) {
        dispatch({
          type: 'SET_CREATE_ERROR',
          error: result.error ?? copy.errors.createFailed,
        })
        return undefined
      }
      projectId = result.projectId
    } else {
      const result = await createEmptyProject({
        name: projectName,
        description: state.projectDescription.trim() || undefined,
        designSystemPresetId: state.selectedPresetId ?? undefined,
        appTarget,
        studioProjectPlan,
      })
      if (!result.success || !result.projectId) {
        dispatch({
          type: 'SET_CREATE_ERROR',
          error: result.error ?? copy.errors.createFailed,
        })
        return undefined
      }
      projectId = result.projectId
    }

    return projectId
  }, [state, dispatch, buildProjectPlan, copy])

  const shouldGenerateApp = Boolean(state.starterPathId)
  const generatePrompt = [
    state.projectDescription.trim() || state.startPrompt.trim(),
    state.projectName.trim() ? `Project name: ${state.projectName.trim()}` : '',
    state.starterPathName ? `Starting path: ${state.starterPathName}` : '',
    `App class: ${state.appClass}`,
    `Runtime targets: ${state.runtimeTargets.join(', ')}`,
    `Security profile: ${state.securityProfile}`,
  ]
    .filter(Boolean)
    .join('\n\n')

  const handleCreate = useCallback(async () => {
    dispatch({ type: 'SET_CREATING', isCreating: true })
    dispatch({ type: 'SET_CREATE_ERROR', error: null })
    setPlanningEvidence(null)
    setProgressMessage(
      shouldGenerateApp
        ? copy.errors.preparingRequest
        : copy.errors.creatingWorkspace,
    )

    try {
      let projectId: string | undefined

      if (shouldGenerateApp) {
        const studioProjectPlan = buildProjectPlan()
        const started = await startAppGenerationFromPrompt(generatePrompt, {
          generationEntry: 'wizard',
          designSystemPreset: state.selectedPresetTokens,
          studioProjectPlan,
          onPlanningProgress: (progress) => {
            setProgressMessage(progress.message)
            const evidence = planningEvidenceFromProgress(progress)
            if (evidence) setPlanningEvidence(evidence)
          },
          onGenerationError: (message) => toast.error(message),
        })
        if (started.status === 'clarification-required') {
          dispatch({
            type: 'SET_CREATE_ERROR',
            error:
              started.sourceSufficiency.questions[0]?.question ??
              copy.errors.clarificationRequired,
          })
          return
        }
        // Task 4 — mirrors the branch immediately above for the same
        // "surface something specific, not a generic failure" reason.
        if (started.status === 'generation-readiness-blocked') {
          const [firstBlocking] = started.readiness.items.filter(
            (item) => item.kind === 'blocking',
          )
          dispatch({
            type: 'SET_CREATE_ERROR',
            error:
              firstBlocking?.explanation ?? copy.errors.notReady,
          })
          return
        }
        setPlanningEvidence(
          planningEvidenceFromMetadata(started.appSpec.decomposePlanning),
        )
        projectId = started.projectId
      } else {
        projectId = await createTemplateOrBlankProject()
      }

      if (!projectId) {
        dispatch({
          type: 'SET_CREATE_ERROR',
          error: copy.errors.noProjectId,
        })
        return
      }

      setCreatedProjectId(projectId)
      setSuccess(true)
      setTimeout(() => {
        if (!shouldGenerateApp) {
          void openServerProject(projectId as string)
        }
        // openServerProject/startAppGenerationFromPrompt update history via
        // pushState/replaceState; dispatch manually so App.tsx unmounts wizard.
        window.dispatchEvent(new PopStateEvent('popstate'))
      }, 700)
    } catch (e) {
      if (e instanceof StudioGenerationBusyError) {
        dispatch({ type: 'SET_CREATE_ERROR', error: e.userMessage })
        return
      }
      console.error('[new-project-wizard] Create failed:', e)
      handleProjectCreateError(e, {
        toastError: (message) => toast.error(message),
        signOut: () => void signOut(),
      })
      dispatch({
        type: 'SET_CREATE_ERROR',
        error: e instanceof Error ? e.message : copy.errors.unexpectedError,
      })
    } finally {
      dispatch({ type: 'SET_CREATING', isCreating: false })
      setProgressMessage(null)
    }
  }, [
    copy,
    createTemplateOrBlankProject,
    buildProjectPlan,
    dispatch,
    generatePrompt,
    openServerProject,
    shouldGenerateApp,
    signOut,
    state.selectedPresetTokens,
  ])

  const panelKey = `step-${step}-${source ?? 'none'}`

  return (
    <div className={styles.wizardPage}>
      <StepperSidebar currentStep={step} />

      <section className={styles.mainArea} aria-label={copy.mainArea}>
        <div className={styles.mainScroll}>
          <div key={panelKey} className={styles.stepPanel}>
            {step === 1 && <Step1Start />}
            {step === 2 && source === 'template' && <Step2Template />}
            {step === 2 && source === 'scratch' && <Step2Scratch />}
            {step === 3 && <Step3Details />}
            {step === 4 && (
              <Step4Review
                success={success}
                createdProjectId={createdProjectId}
                mode={shouldGenerateApp ? 'generate' : 'project'}
                progressMessage={progressMessage}
                planningEvidence={planningEvidence}
                onCreateClick={handleCreate}
              />
            )}
          </div>
        </div>

        <FooterNav
          onCancel={handleCancel}
          onCreate={handleCreate}
          createLabel={
            shouldGenerateApp
              ? copy.footer.createWithCogo
              : copy.footer.createProject
          }
          creatingLabel={copy.footer.creating}
        />
      </section>
    </div>
  )
}

export function NewProjectWizardPage() {
  return (
    <WizardProvider>
      <WizardContent />
    </WizardProvider>
  )
}
